/*
  Free nootropic label calculator (sections/nr-nootropic-tool.liquid).

  Step 1 decodes one label. For a herb or mushroom: amount × % gives each
  marker in mg (withanolides, bacosides...), and amount × ratio gives the
  dried herb an extract was made from. For a compound: amount × purity gives
  the form itself, and its share of weight gives what it's counted as (NALT
  to L-tyrosine, citicoline to choline...). Step 2 keeps a list of products
  and totals the same marker or compound across them.

  Each ingredient's markers, named extracts and forms arrive as the
  customiser's textarea lines and are parsed here (the format is in the
  section's setting info). Choices and the list are kept in localStorage on
  the visitor's own device only. Nothing here says how much to take.
*/
if (!customElements.get('nr-nootropic-tool')) {
  customElements.define(
    'nr-nootropic-tool',
    class NrNootropicTool extends HTMLElement {
      connectedCallback() {
        const configElement = this.querySelector('[data-config]');
        if (!configElement) return;
        try {
          this.config = JSON.parse(configElement.textContent);
        } catch (error) {
          return;
        }
        this.ingredients = new Map(this.config.ingredients.map((item) => [item.id, NrNootropicTool.parse(item)]));
        if (this.ingredients.size === 0) return;

        this.el = {
          app: this.querySelector('[data-app]'),
          ingredient: this.querySelector('[data-ingredient]'),
          amount: this.querySelector('[data-amount]'),
          servings: this.querySelector('[data-servings]'),
          namedField: this.querySelector('[data-named-field]'),
          named: this.querySelector('[data-named]'),
          extractFields: this.querySelector('[data-extract-fields]'),
          compoundFields: this.querySelector('[data-compound-fields]'),
          form: this.querySelector('[data-form]'),
          purity: this.querySelector('[data-purity]'),
          stats: this.querySelector('[data-stats]'),
          notes: this.querySelector('[data-notes]'),
          links: this.querySelector('[data-links]'),
          productName: this.querySelector('[data-product-name]'),
          list: this.querySelector('[data-list]'),
          empty: this.querySelector('[data-empty]'),
          totalsWrap: this.querySelector('[data-totals-wrap]'),
          totals: this.querySelector('[data-totals]'),
        };
        this.entries = [];
        this.uid = 'nr-nootropic-' + Math.random().toString(36).slice(2, 8);

        const saved = this.restore();
        this.drawFields(saved);
        this.bind();
        this.render();
        this.renderList();
        this.el.app.classList.add('is-ready');
      }

      /* ---------- State ---------- */

      restore() {
        let saved = null;
        try {
          saved = JSON.parse(window.localStorage.getItem(NrNootropicTool.storageKey) || 'null');
        } catch (error) {
          saved = null;
        }
        if (!saved) return null;

        if (this.ingredients.has(saved.id)) this.el.ingredient.value = saved.id;
        if (Number(saved.amount) > 0) this.el.amount.value = saved.amount;
        if (Number(saved.servings) > 0) this.el.servings.value = saved.servings;
        if (Array.isArray(saved.entries)) {
          this.entries = saved.entries.filter((entry) => entry && entry.name && Array.isArray(entry.outputs));
        }
        return saved.id === this.el.ingredient.value ? saved : null;
      }

      save() {
        const values = this.values();
        try {
          window.localStorage.setItem(
            NrNootropicTool.storageKey,
            JSON.stringify({
              id: this.el.ingredient.value,
              amount: this.el.amount.value,
              servings: this.el.servings.value,
              percents: values.percents,
              ratio: values.ratio || '',
              named: values.named,
              form: values.form ? values.form.name : '',
              purity: this.el.purity.value,
              entries: this.entries,
            })
          );
        } catch (error) {
          /* Private browsing or storage turned off: the tool still works. */
        }
      }

      bind() {
        this.addEventListener('change', (event) => {
          if (!event.target.closest('[data-app]')) return;
          this.track();
          if (event.target === this.el.ingredient) {
            this.drawFields(null);
          } else if (event.target.matches('[data-named-pick]')) {
            this.applyNamed(event.target.value);
          }
          this.update();
        });

        this.addEventListener('input', (event) => {
          if (!event.target.closest('[data-app]') || !event.target.matches('input[type="number"]')) return;
          this.track();
          // A figure typed by hand no longer matches the named extract picked.
          if (event.target.matches('[data-percent]')) {
            this.el.named.querySelectorAll('[data-named-pick]').forEach((input) => {
              input.checked = false;
            });
          }
          this.update();
        });

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-add]')) this.add();

          const remove = event.target.closest('[data-remove]');
          if (remove) {
            this.entries.splice(Number(remove.dataset.remove), 1);
            this.save();
            this.renderList();
          }

          const link = event.target.closest('[data-nr-tool-product]');
          if (link) NrNootropicTool.publish('nr_tool_product_click', { tool: 'nootropic', product: link.dataset.nrToolProduct });
        });
      }

      update() {
        this.save();
        this.render();
      }

      track() {
        if (this.tracked) return;
        this.tracked = true;
        NrNootropicTool.publish('nr_tool_use', { tool: 'nootropic' });
      }

      current() {
        return this.ingredients.get(this.el.ingredient.value) || this.ingredients.values().next().value;
      }

      // What's typed in now, as numbers. An empty box is "not on the label".
      values() {
        const ingredient = this.current();
        const percents = {};
        this.el.extractFields.querySelectorAll('[data-percent]').forEach((input) => {
          if (input.value !== '') percents[input.dataset.percent] = input.value;
        });
        const ratio = this.el.extractFields.querySelector('[data-ratio]');
        const named = this.el.named.querySelector('[data-named-pick]:checked');
        const servings = Number(this.el.servings.value);
        return {
          ingredient: ingredient,
          amount: Number(this.el.amount.value) || 0,
          servings: servings > 0 ? servings : 1,
          percents: percents,
          ratio: ratio ? Number(ratio.value) || 0 : 0,
          named: named ? named.value : '',
          form: ingredient.forms.find((form) => form.name === this.el.form.value) || ingredient.forms[0],
          purity: this.el.purity.value === '' ? 100 : Number(this.el.purity.value) || 0,
          purityGiven: this.el.purity.value !== '',
        };
      }

      /* ---------- Fields for the picked ingredient ---------- */

      drawFields(saved) {
        const ingredient = this.current();
        const text = this.config.text;
        const compound = ingredient.kind === 'compound';

        this.el.compoundFields.hidden = !compound;
        this.el.extractFields.hidden = compound;
        this.el.namedField.hidden = compound || ingredient.named.length === 0;

        // Named extracts: picking one fills in its figures.
        this.el.named.replaceChildren(
          ...ingredient.named.map((named) => {
            const label = document.createElement('label');
            label.className = 'nr-tool__choice';
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = this.uid + '-named';
            input.value = named.name;
            input.dataset.namedPick = '';
            input.checked = Boolean(saved && saved.named === named.name);
            const span = document.createElement('span');
            span.textContent = named.name;
            label.append(input, span);
            return label;
          })
        );

        // A % box for each marker, then the ratio.
        const percents = (saved && saved.percents) || {};
        const fields = ingredient.markers.map((marker) =>
          NrNootropicTool.field(NrNootropicTool.fill(text.percent, { marker: NrNootropicTool.cap(marker.name) }), {
            percent: marker.name,
            value: percents[marker.name] || '',
            max: 100,
          })
        );
        fields.push(NrNootropicTool.field(text.ratio, { ratio: '', value: saved && saved.ratio ? saved.ratio : '' }));
        this.el.extractFields.replaceChildren(...(compound ? [] : fields));

        // Compounds: the form on the label and its purity.
        this.el.form.replaceChildren(
          ...ingredient.forms.map((form) => {
            const option = document.createElement('option');
            option.value = form.name;
            option.textContent = form.name;
            return option;
          })
        );
        if (saved && ingredient.forms.some((form) => form.name === saved.form)) this.el.form.value = saved.form;
        this.el.purity.value = saved && saved.purity ? saved.purity : '';
      }

      applyNamed(name) {
        const named = this.current().named.find((item) => item.name === name);
        if (!named) return;
        this.el.extractFields.querySelectorAll('[data-percent]').forEach((input) => {
          const value = named.values[input.dataset.percent];
          input.value = value === undefined ? '' : value;
        });
      }

      /* ---------- Maths ---------- */

      // Stats, notes and the per-day amounts for the list, from the label's figures.
      decode() {
        const v = this.values();
        const ingredient = v.ingredient;
        const text = this.config.text;
        const fmt = NrNootropicTool.mg;
        const result = { stats: [], notes: [], outputs: [] };
        const after = [];

        if (ingredient.kind === 'compound') {
          const form = v.form;
          if (v.amount > 0 && form) {
            const pure = (v.amount * v.purity) / 100;
            const active = (pure * form.share) / 100;
            result.stats.push({
              label: NrNootropicTool.fill(text.statActive, { active: NrNootropicTool.cap(ingredient.active) }),
              value: fmt(active * v.servings),
            });
            if (form.share !== 100) {
              result.stats.push({ label: NrNootropicTool.fill(text.statForm, { form: form.name }), value: fmt(pure * v.servings) });
            }
            if (v.purity !== 100) result.stats.push({ label: text.statLabel, value: fmt(v.amount * v.servings) });
            if (v.purityGiven && v.purity !== 100) {
              result.notes.push(NrNootropicTool.fill(text.purity, { purity: v.purity, amount: fmt(v.amount), pure: fmt(pure), form: NrNootropicTool.inline(form.name) }));
            }
            if (form.share !== 100) {
              result.notes.push(
                NrNootropicTool.fill(text.form, { amount: fmt(pure), form: NrNootropicTool.inline(form.name), mg: fmt(active), active: ingredient.active, share: form.share })
              );
              after.push(text.weight);
            }
            result.outputs.push({ name: ingredient.active, mg: active * v.servings });
          }
          if (ingredient.purities.length > 0 && !v.purityGiven) {
            result.notes.push(
              NrNootropicTool.fill(text.commonPurity, { figures: this.joinOr(ingredient.purities.map((figure) => figure + '%')) })
            );
          }
        } else {
          const given = ingredient.markers.filter((marker) => Number(v.percents[marker.name]) > 0);
          const missing = ingredient.markers.filter((marker) => !(Number(v.percents[marker.name]) > 0));
          if (v.amount > 0) {
            given.forEach((marker) => {
              const percent = Number(v.percents[marker.name]);
              const each = (v.amount * percent) / 100;
              result.stats.push({
                label: NrNootropicTool.fill(text.statMarker, { marker: NrNootropicTool.cap(marker.name) }),
                value: fmt(each * v.servings),
              });
              result.notes.push(NrNootropicTool.fill(text.marker, { mg: fmt(each), marker: marker.name, percent: percent, amount: fmt(v.amount) }));
              result.outputs.push({ name: marker.name, mg: each * v.servings });
            });
            result.stats.push({ label: text.statExtract, value: fmt(v.amount * v.servings) });

            if (v.ratio > 0) {
              const herb = NrNootropicTool.mass(v.amount * v.ratio * v.servings);
              result.stats.push({ label: NrNootropicTool.fill(text.statHerb, { part: ingredient.part }), value: herb.value, unit: herb.unit });
              const each = NrNootropicTool.mass(v.amount * v.ratio);
              result.notes.push(
                NrNootropicTool.fill(text.herb, { ratio: v.ratio, herb: each.value + ' ' + each.unit, part: ingredient.part, amount: fmt(v.amount) })
              );
            }
          }
          const markerNames = this.joinOr(ingredient.markers.map((marker) => marker.name));
          if (given.length === 0 && ingredient.markers.length > 0) {
            result.notes.push(NrNootropicTool.fill(v.ratio > 0 ? text.ratioOnly : text.none, { marker: markerNames }));
          }
          missing.forEach((marker) => {
            if (marker.common.length === 0) return;
            result.notes.push(
              NrNootropicTool.fill(text.common, { figures: this.joinOr(marker.common.map((figure) => figure + '%')), marker: marker.name })
            );
          });
          if (v.ratio > 0 && ingredient.ratios) result.notes.push(NrNootropicTool.fill(text.commonRatios, { ratios: ingredient.ratios }));
          if (given.length === 0 && ingredient.clear) after.push(ingredient.clear);
        }

        result.notes.push(...after, ...ingredient.notes);
        return result;
      }

      /* ---------- Drawing ---------- */

      render() {
        const ingredient = this.current();
        const result = this.decode();

        this.el.stats.replaceChildren(...result.stats.map((stat) => NrNootropicTool.stat(stat.label, stat.value, stat.unit || 'mg')));
        this.el.stats.hidden = result.stats.length === 0;
        this.el.stats.classList.toggle('nr-tool__stats--2', result.stats.length === 2 || result.stats.length === 4);
        NrNootropicTool.list(this.el.notes, result.notes);

        const links = [];
        if (ingredient.atlas && this.config.text.atlas) links.push(NrNootropicTool.link(ingredient.atlas, this.config.text.atlas));
        if (ingredient.link && ingredient.linkLabel) links.push(NrNootropicTool.link(ingredient.link, ingredient.linkLabel));
        this.el.links.replaceChildren(...links);
        this.el.links.hidden = links.length === 0;
      }

      add() {
        const v = this.values();
        if (!(v.amount > 0)) {
          this.el.amount.focus();
          return;
        }
        const result = this.decode();
        this.entries.push({
          name: this.el.productName.value.trim() || v.ingredient.name,
          amount: v.amount,
          servings: v.servings,
          outputs: result.outputs.map((output) => ({ name: output.name, mg: Math.round(output.mg * 10) / 10 })),
        });
        this.el.productName.value = '';
        this.track();
        this.save();
        this.renderList();
      }

      renderList() {
        const text = this.config.text;
        const fmt = NrNootropicTool.mg;

        this.el.list.replaceChildren(
          ...this.entries.map((entry, index) => {
            const li = document.createElement('li');

            const name = document.createElement('span');
            name.className = 'nr-tool__list-name';
            const sub = document.createElement('small');
            sub.textContent = NrNootropicTool.fill(text.perDay, { amount: fmt(entry.amount), servings: entry.servings });
            name.append(entry.name, sub);

            const mg = document.createElement('span');
            mg.className = 'nr-tool__list-mg';
            const lines = entry.outputs.length > 0 ? entry.outputs.map((output) => fmt(output.mg) + ' mg ' + output.name) : [text.noFigure];
            lines.forEach((line) => {
              const span = document.createElement('span');
              span.textContent = line;
              mg.append(span);
            });

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'nr-tool__remove';
            remove.dataset.remove = index;
            remove.setAttribute('aria-label', text.remove + ': ' + entry.name);
            remove.textContent = '×';

            li.append(name, mg, remove);
            return li;
          })
        );
        this.el.empty.hidden = this.entries.length > 0;

        // The same marker or compound, added up across every product.
        const totals = new Map();
        this.entries.forEach((entry) => {
          entry.outputs.forEach((output) => {
            const key = output.name.toLowerCase();
            const total = totals.get(key) || { name: output.name, mg: 0 };
            total.mg += Number(output.mg) || 0;
            totals.set(key, total);
          });
        });
        this.el.totals.replaceChildren(
          ...Array.from(totals.values()).map((total) => NrNootropicTool.stat(NrNootropicTool.cap(total.name), fmt(total.mg), 'mg'))
        );
        this.el.totals.classList.toggle('nr-tool__stats--2', totals.size === 2 || totals.size === 4);
        this.el.totalsWrap.hidden = totals.size === 0;
      }

      joinOr(items) {
        if (items.length < 2) return items.join('');
        return items.slice(0, -1).join(', ') + ' ' + (this.config.text.or || 'or') + ' ' + items[items.length - 1];
      }

      /* ---------- Helpers ---------- */

      static get storageKey() {
        return 'nr-nootropic-tool';
      }

      // An ingredient block's textarea lines, as lists.
      static parse(item) {
        const lines = (value) =>
          String(value || '')
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line);
        const figures = (value) =>
          String(value || '')
            .split(',')
            .map((figure) => figure.trim())
            .filter((figure) => figure && !Number.isNaN(Number(figure)));
        const cells = (line) => {
          const at = line.indexOf('|');
          return at === -1 ? [line, ''] : [line.slice(0, at).trim(), line.slice(at + 1).trim()];
        };

        return {
          ...item,
          markers: lines(item.markers).map((line) => {
            const [name, common] = cells(line);
            return { name: name, common: figures(common) };
          }),
          named: lines(item.named)
            .map((line) => {
              const [name, rest] = cells(line);
              const values = {};
              rest.split(';').forEach((pair) => {
                const match = pair.trim().match(/^(.*\S)\s+(\d+(?:\.\d+)?)$/);
                if (match) values[match[1]] = match[2];
              });
              return { name: name, values: values };
            })
            .filter((named) => Object.keys(named.values).length > 0),
          forms: lines(item.forms)
            .map((line) => {
              const [name, share] = cells(line);
              return { name: name, share: Number(share) };
            })
            .filter((form) => form.name && form.share > 0),
          purities: figures(item.purities),
          notes: lines(item.notes),
        };
      }

      static field(labelText, data) {
        const label = document.createElement('label');
        label.className = 'nr-tool__field';
        const span = document.createElement('span');
        span.className = 'nr-tool__label';
        span.textContent = labelText;
        const input = document.createElement('input');
        input.className = 'nr-tool__input';
        input.type = 'number';
        input.inputMode = 'decimal';
        input.min = '0';
        input.step = 'any';
        if (data.max) input.max = String(data.max);
        if ('percent' in data) input.dataset.percent = data.percent;
        if ('ratio' in data) input.dataset.ratio = '';
        input.value = data.value;
        label.append(span, input);
        return label;
      }

      static stat(label, value, unit) {
        const wrap = document.createElement('div');
        wrap.className = 'nr-tool__stat';
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        const small = document.createElement('small');
        small.textContent = unit;
        dd.append(value + ' ', small);
        wrap.append(dt, dd);
        return wrap;
      }

      static link(href, label) {
        const a = document.createElement('a');
        a.href = href;
        a.textContent = label;
        return a;
      }

      // Milligrams: one decimal place under 10 mg, whole numbers above.
      static mg(value) {
        const n = Number(value) || 0;
        return n.toLocaleString('en-GB', { maximumFractionDigits: n < 10 ? 1 : 0 });
      }

      // A weight in mg, or in g from 1,000 mg up.
      static mass(mgValue) {
        if (mgValue >= 1000) {
          return { value: (mgValue / 1000).toLocaleString('en-GB', { maximumFractionDigits: 1 }), unit: 'g' };
        }
        return { value: NrNootropicTool.mg(mgValue), unit: 'mg' };
      }

      static cap(value) {
        const s = String(value || '');
        return s.charAt(0).toUpperCase() + s.slice(1);
      }

      // A name in the middle of a sentence: "Acetyl-L-carnitine" becomes
      // "acetyl-L-carnitine", while "L-carnitine" and "NALT" stay as they are.
      static inline(value) {
        const s = String(value || '');
        return /^[A-Z][a-z]/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s;
      }

      static list(target, lines) {
        target.replaceChildren(
          ...lines
            .filter((line) => line)
            .map((line) => {
              const li = document.createElement('li');
              li.textContent = line;
              return li;
            })
        );
      }

      static fill(template, values) {
        return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
      }

      static publish(name, detail) {
        try {
          if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail);
        } catch (error) {
          /* Tracking must never break the tool. */
        }
      }
    }
  );
}
